import api from "./axios";

/* ----------------------- tiny helpers ----------------------- */
function asPositiveInt(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.trunc(n);
}

function normalizePhone(phone) {
  if (phone == null) return null;
  let p = String(phone).replace(/\D/g, "");
  if (p.length > 10) p = p.slice(-10);
  return /^\d{10}$/.test(p) ? p : null;
}

function normalizeEmail(email) {
  if (email == null) return null;
  const e = String(email).trim();
  if (!e) return null;
  return e.includes("@") ? e : null;
}

/**
 * Accepts:
 *  - { userId, phoneNumber, email }
 *  - { id, phoneNumber, email }
 *  - string (userId or phone or email)
 */
function buildUserKey(input) {
  if (typeof input === "string") {
    const s = input.trim();
    if (!s) return { userId: null, phone: null, email: null };

    const email = normalizeEmail(s);
    if (email) return { userId: null, phone: null, email };

    const maybeId = asPositiveInt(s);
    if (maybeId) return { userId: maybeId, phone: null, email: null };

    const phone = normalizePhone(s);
    return { userId: null, phone, email: null };
  }

  const userId = asPositiveInt(input?.userId) ?? asPositiveInt(input?.id) ?? null;
  const phone =
    normalizePhone(input?.phoneNumber) ??
    normalizePhone(input?.phone) ??
    null;
  const email =
    normalizeEmail(input?.email) ??
    normalizeEmail(input?.userEmail) ??
    null;

  return { userId, phone, email };
}

function getStoredUserKey() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return { userId: null, phone: null, email: null };
    return buildUserKey(JSON.parse(raw));
  } catch {
    return { userId: null, phone: null, email: null };
  }
}

async function resolveKeyFromTokenIfPossible() {
  try {
    const token = localStorage.getItem("token");
    if (!token) return { userId: null, phone: null, email: null };

    if (!api.defaults.headers.common.Authorization) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
    }

    const candidates = [
      "/api/users/current-user",
      "/users/current-user",
      "/api/users/me",
      "/users/me",
      "/me",
    ];

    let data = null;
    for (const url of candidates) {
      try {
        const r = await api.get(url);
        data = r.data;
        if (data) break;
      } catch {}
    }

    const k = buildUserKey(data);
    if (k.userId || k.phone || k.email) {
      // merge into local storage
      let existing = null;
      try {
        const raw = localStorage.getItem("user");
        existing = raw ? JSON.parse(raw) : null;
      } catch {}
      localStorage.setItem(
        "user",
        JSON.stringify({ ...(existing || {}), ...(data || {}) })
      );
      return k;
    }
  } catch {}
  return { userId: null, phone: null, email: null };
}

async function assertKeyAsync(keyLike) {
  const direct = buildUserKey(keyLike);
  if (direct.userId || direct.phone || direct.email) return direct;

  const stored = getStoredUserKey();
  if (stored.userId || stored.phone || stored.email) return stored;

  const fromToken = await resolveKeyFromTokenIfPossible();
  if (fromToken.userId || fromToken.phone || fromToken.email) return fromToken;

  const err = new Error("Missing user identity (need userId OR phone/email).");
  err.code = "AUTH_REQUIRED";
  throw err;
}

/* ----------------------- axios helpers (cancel flow robustness) ----------------------- */
/**
 * Some backends accept DELETE with body, some don't.
 * We'll:
 * 1) Try DELETE (with optional text/plain body)
 * 2) If 405/415, fallback to POST to the same URL (optional)
 */
async function requestCancel(methodUrl, reasonText) {
  const cfg =
    reasonText != null && String(reasonText).trim()
      ? {
          data: String(reasonText),
          headers: { "Content-Type": "text/plain" },
        }
      : undefined;
  const baseURL = api?.defaults?.baseURL || "";

  try {
    console.log("[bookingApi] cancel request", {
      method: "DELETE",
      baseURL,
      url: methodUrl,
      hasReason: Boolean(cfg?.data),
    });
    const r = await api.delete(methodUrl, cfg);
    return r?.data;
  } catch (e) {
    const status = e?.response?.status;
    console.warn("[bookingApi] cancel delete failed", {
      baseURL,
      url: methodUrl,
      status,
      response: e?.response?.data,
    });
    // fallback when DELETE not allowed or body not supported
    if (status === 405 || status === 415) {
      console.log("[bookingApi] cancel fallback", {
        method: "POST",
        baseURL,
        url: methodUrl,
        hasReason: Boolean(cfg?.data),
      });
      const r2 = await api.post(methodUrl, cfg?.data ?? null, {
        headers: cfg?.headers ?? undefined,
      });
      return r2?.data;
    }
    throw e;
  }
}

function normalizeCancelResponse(data) {
  if (typeof data === "string") return data;
  if (data && typeof data === "object") return data?.message ?? data?.otp ?? "OK";
  return "OK";
}

function pickNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/* ----------------------- loader endpoints ----------------------- */
export async function getUserBookingsWithRequestsById(userId) {
  const idNum = asPositiveInt(userId);
  if (!idNum) throw new Error("Missing or invalid userId");

  const { data } = await api.get(
    `/bookings/user/id/${encodeURIComponent(String(idNum))}/with-requests`
  );
  return {
    finalBookings: Array.isArray(data?.finalBookings) ? data.finalBookings : [],
    pendingRequests: Array.isArray(data?.pendingRequests) ? data.pendingRequests : [],
  };
}

export async function getUserBookingsWithRequestsByPhone(phone) {
  const p = normalizePhone(phone);
  if (!p) throw new Error("Missing or invalid phone");

  const { data } = await api.get(
    `/bookings/user/phone/${encodeURIComponent(p)}/with-requests`
  );
  return {
    finalBookings: Array.isArray(data?.finalBookings) ? data.finalBookings : [],
    pendingRequests: Array.isArray(data?.pendingRequests) ? data.pendingRequests : [],
  };
}

export async function getUserBookingsWithRequestsForUser(keyLike) {
  const k = await assertKeyAsync(keyLike);
  if (k.userId) return getUserBookingsWithRequestsById(k.userId);
  if (k.phone) return getUserBookingsWithRequestsByPhone(k.phone);

  // email cannot load /with-requests (no endpoint)
  const err = new Error(
    "Need userId or phone to load bookings. Email-only user is not supported for with-requests."
  );
  err.code = "AUTH_REQUIRED";
  throw err;
}

export async function resolveUserIdForAuthenticatedUser(keyLike) {
  const direct = buildUserKey(keyLike);
  if (direct.userId) return direct.userId;

  const stored = getStoredUserKey();
  if (stored.userId) return stored.userId;

  const resolved = await resolveKeyFromTokenIfPossible();
  if (resolved.userId) return resolved.userId;

  return null;
}

export async function getUserBookingActiveGroupsByUserId(userId) {
  const idNum = asPositiveInt(userId);
  if (!idNum) throw new Error("Missing or invalid userId");

  const { data } = await api.get(
    `/bookings/user/id/${encodeURIComponent(String(idNum))}/groups/active`
  );
  console.log("[bookingApi] active groups raw response", {
    userId: idNum,
    data,
  });

  return {
    groups: Array.isArray(data?.groups) ? data.groups : [],
    hasMore: Boolean(data?.hasMore),
    nextPage: pickNumber(data?.nextPage),
  };
}

export async function getUserBookingHistoryGroupsByUserId(userId, tab, page = 0, size = 10) {
  const idNum = asPositiveInt(userId);
  if (!idNum) throw new Error("Missing or invalid userId");

  const safeTab = String(tab || "").toLowerCase() === "cancelled" ? "cancelled" : "completed";

  const { data } = await api.get(
    `/bookings/user/id/${encodeURIComponent(String(idNum))}/groups/history`,
    { params: { tab: safeTab, page, size } }
  );
  console.log("[bookingApi] history groups raw response", {
    userId: idNum,
    tab: safeTab,
    page,
    size,
    data,
  });

  return {
    groups: Array.isArray(data?.groups) ? data.groups : [],
    hasMore: Boolean(data?.hasMore),
    nextPage: pickNumber(data?.nextPage),
  };
}

/* ----------------------- place endpoints ----------------------- */
export async function placeBookingByUserId(userId) {
  const idNum = asPositiveInt(userId);
  if (!idNum) throw new Error("Missing or invalid userId");

  const { data } = await api.post(
    `/bookings/${encodeURIComponent(String(idNum))}/place-by-id`
  );
  return normalizeCancelResponse(data);
}

export async function placeBookingForUser(keyLike) {
  const k = await assertKeyAsync(keyLike);

  if (k.userId) return placeBookingByUserId(k.userId);

  // ✅ fallback to legacy backend endpoint if id missing
  const identifier = k.phone || k.email;
  if (!identifier) {
    const err = new Error("userId/phone/email required to place booking.");
    err.code = "AUTH_REQUIRED";
    throw err;
  }

  const { data } = await api.post(
    `/bookings/${encodeURIComponent(String(identifier))}/place`
  );
  return normalizeCancelResponse(data);
}

/* ----------------------- cancel FINAL booking endpoints ----------------------- */
/**
 * Backend cancel flow:
 *  DELETE /bookings/{bookingId}/cancel-by-id/{userId}
 * (optional legacy) DELETE /bookings/{bookingId}/cancel/{identifier}
 *
 * We also allow optional `reason` which will be sent as text/plain if provided.
 */
export async function cancelBookingByUserId(bookingId, userId, reason) {
  const idNum = asPositiveInt(userId);
  if (!idNum) throw new Error("Missing or invalid userId");
  if (!bookingId) throw new Error("Missing bookingId");

  const url = `/bookings/${encodeURIComponent(String(bookingId))}/cancel-by-id/${encodeURIComponent(
    String(idNum)
  )}`;

  const data = await requestCancel(url, reason);
  return normalizeCancelResponse(data);
}

export async function cancelBookingForUser(bookingId, keyLike, reason) {
  const k = await assertKeyAsync(keyLike);

  if (k.userId) return cancelBookingByUserId(bookingId, k.userId, reason);

  // ✅ fallback to legacy backend endpoint (works with phone OR email)
  const identifier = k.phone || k.email;
  if (!identifier) {
    const err = new Error("userId/phone/email required to cancel booking.");
    err.code = "AUTH_REQUIRED";
    throw err;
  }

  const url = `/bookings/${encodeURIComponent(String(bookingId))}/cancel/${encodeURIComponent(
    String(identifier)
  )}`;

  const data = await requestCancel(url, reason);
  return normalizeCancelResponse(data);
}

/* ----------------------- cancel REQUEST endpoints ----------------------- */
/**
 * Backend cancel flow:
 *  DELETE /bookings/request/{requestId}/cancel-by-id/{userId}
 * (optional legacy) DELETE /bookings/request/{requestId}/cancel/{identifier}
 */
export async function cancelRequestByUserId(requestId, userId, reason) {
  const idNum = asPositiveInt(userId);
  if (!idNum) throw new Error("Missing or invalid userId");
  if (!requestId) throw new Error("Missing requestId");

  const url = `/bookings/request/${encodeURIComponent(
    String(requestId)
  )}/cancel-by-id/${encodeURIComponent(String(idNum))}`;

  const data = await requestCancel(url, reason);
  return normalizeCancelResponse(data);
}

export async function cancelRequestForUser(requestId, keyLike, reason) {
  const k = await assertKeyAsync(keyLike);

  if (k.userId) return cancelRequestByUserId(requestId, k.userId, reason);

  const identifier = k.phone || k.email;
  if (!identifier) {
    const err = new Error("userId/phone/email required to cancel request.");
    err.code = "AUTH_REQUIRED";
    throw err;
  }

  const url = `/bookings/request/${encodeURIComponent(
    String(requestId)
  )}/cancel/${encodeURIComponent(String(identifier))}`;

  const data = await requestCancel(url, reason);
  return normalizeCancelResponse(data);
}

export async function cancelScheduledPlanByUserId(planKey, userId, reason) {
  const idNum = asPositiveInt(userId);
  const normalizedPlanKey = String(planKey || "").trim();
  if (!idNum) throw new Error("Missing or invalid userId");
  if (!normalizedPlanKey) throw new Error("Missing planKey");

  const url = `/bookings/plans/${encodeURIComponent(
    normalizedPlanKey
  )}/cancel-by-id/${encodeURIComponent(String(idNum))}`;

  const data = await requestCancel(url, reason);
  return normalizeCancelResponse(data);
}

export async function cancelScheduledPlanForUser(planKey, keyLike, reason) {
  const normalizedPlanKey = String(planKey || "").trim();
  if (!normalizedPlanKey) throw new Error("Missing planKey");

  const k = await assertKeyAsync(keyLike);

  if (k.userId) return cancelScheduledPlanByUserId(normalizedPlanKey, k.userId, reason);

  const identifier = k.phone || k.email;
  if (!identifier) {
    const err = new Error("userId/phone/email required to cancel scheduled plan.");
    err.code = "AUTH_REQUIRED";
    throw err;
  }

  const url = `/bookings/plans/${encodeURIComponent(
    normalizedPlanKey
  )}/cancel/${encodeURIComponent(String(identifier))}`;

  const data = await requestCancel(url, reason);
  return normalizeCancelResponse(data);
}

/* ----------------------- compatibility wrappers ----------------------- */
export async function placeBooking(userKeyLike) {
  return placeBookingForUser(userKeyLike);
}
export async function cancelBooking(bookingId, userKeyLike, reason) {
  return cancelBookingForUser(bookingId, userKeyLike, reason);
}
export async function cancelRequest(requestId, userKeyLike, reason) {
  return cancelRequestForUser(requestId, userKeyLike, reason);
}
